import { metersBetween, toLocal, type LatLng } from '@/features/detection/domain/geometry'
import { bearingOf } from './pathGeometry'

// Représentation visuelle du tracteur et caméra de la carte, séparées de la position réelle. La position réelle (GPS
// horodaté) continue d'alimenter détection, télémétrie et journal sans aucune modification : ce module ne produit que
// ce qui est dessiné. Fonction pure, testable sans Google Maps.

export type CameraMode =
  /** Déplacement dans la rue : le symbole suit la progression fiable, la carte s'oriente selon le tronçon prévu. */
  | 'TRAVEL'
  /** Résidence confirmée par le moteur : symbole stabilisé près de l'entrée, caméra figée. */
  | 'WORK'
  /** Le moteur confirme une résidence voisine : glissement court du symbole, caméra inchangée. */
  | 'TRANSITION'
  /** Sortie confirmée : le symbole rejoint la position de déplacement, orientation conservée. */
  | 'LEAVING'
  /** Mission en pause : tout est figé. */
  | 'PAUSED'

export interface CameraInput {
  now: number
  /** Dernière position réelle acceptable (précision connue, âge contrôlé par l'appelant); `null` : aucune. */
  fix: (LatLng & { accuracyM: number | null }) | null
  /** Position réelle récente et assez précise. Sinon : dernière représentation, avec incertitude. */
  gpsFresh: boolean
  /** Position projetée sur le parcours prévu et orientation du tronçon; `null` : hors parcours ou sans parcours. */
  progress: { point: LatLng; bearing: number } | null
  /** Résidence confirmée par le moteur (entrée dans sa zone); `null` : aucune. Jamais un simple chevauchement. */
  work: { itemId: string; entry: LatLng } | null
  paused: boolean
}

export interface CameraState {
  mode: CameraMode
  symbol: LatLng | null
  /** Orientation appliquée à la carte (degrés, déroulée : pas de saut de 359° à 0°). */
  heading: number
  workItemId: string | null
  animation: { from: LatLng; to: LatLng; startedAt: number; durationMs: number } | null
  /** Ancre du cap de secours (sans parcours) : dernier point d'où le cap a été mesuré. */
  fallbackAnchor: LatLng | null
  lastNow: number | null
}

export interface CameraOutput {
  mode: CameraMode
  symbol: LatLng | null
  heading: number
  /** Symbole stabilisé (intervention en cours ou pause) : indication « vue stabilisée ». */
  stabilized: boolean
  /** Position réelle absente, périmée ou imprécise : aucune progression ni transition inventée. */
  uncertain: boolean
  /** Orientation issue du parcours prévu (sinon : cap de secours, ou nord). */
  headingSource: 'PATH' | 'MOVEMENT' | 'FROZEN'
}

export const TRANSITION_MS = 1200
export const LEAVING_MS = 2000
/** Rotation maximale de la carte (degrés par seconde). */
export const MAX_TURN_DEG_PER_S = 40
/** Écart d'orientation ignoré (degrés) : aucun frémissement de la carte. */
export const HEADING_DEADBAND_DEG = 6
/** Déplacement minimal (m) et précision maximale pour mesurer un cap de secours sans parcours. */
export const FALLBACK_HEADING_MIN_MOVE_M = 25

export const initialCamera: CameraState = {
  mode: 'TRAVEL',
  symbol: null,
  heading: 0,
  workItemId: null,
  animation: null,
  fallbackAnchor: null,
  lastNow: null,
}

/** Écart signé le plus court (−180, 180]. */
export function angleDelta(from: number, to: number): number {
  const delta = (((to - from) % 360) + 540) % 360 - 180
  return delta === -180 ? 180 : delta
}

/** Rapproche `current` de `target` au plus de `maxStep` degrés, en ignorant les écarts sous la zone morte. */
export function turnToward(current: number, target: number, maxStep: number, deadband = HEADING_DEADBAND_DEG): number {
  const delta = angleDelta(current, target)
  if (Math.abs(delta) <= deadband) return current
  return current + Math.sign(delta) * Math.min(Math.abs(delta), maxStep)
}

function interpolate(from: LatLng, to: LatLng, t: number): LatLng {
  const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
  return { lat: from.lat + (to.lat - from.lat) * eased, lng: from.lng + (to.lng - from.lng) * eased }
}

function animated(state: CameraState, now: number): { point: LatLng | null; done: boolean } {
  const animation = state.animation
  if (!animation) return { point: state.symbol, done: true }
  const t = Math.min(1, (now - animation.startedAt) / animation.durationMs)
  return { point: interpolate(animation.from, animation.to, t), done: t >= 1 }
}

export function stepCamera(state: CameraState, input: CameraInput): { state: CameraState; output: CameraOutput } {
  const dt = state.lastNow === null ? 0 : Math.max(0, (input.now - state.lastNow) / 1000)
  const maxStep = MAX_TURN_DEG_PER_S * Math.min(dt, 2)
  const travelPoint = input.gpsFresh ? (input.progress?.point ?? input.fix) : null
  let next: CameraState = { ...state, lastNow: input.now }
  let headingSource: CameraOutput['headingSource'] = 'FROZEN'

  if (input.paused) {
    next = { ...next, mode: 'PAUSED', animation: null }
  } else if (input.work) {
    const entry = input.work.entry
    if (state.mode === 'WORK' || state.mode === 'TRANSITION') {
      if (state.workItemId !== input.work.itemId) {
        // Nouvelle résidence confirmée par le moteur : glissement depuis le symbole actuel, caméra inchangée.
        const from = animated(state, input.now).point ?? entry
        next = {
          ...next,
          mode: 'TRANSITION',
          workItemId: input.work.itemId,
          animation: { from, to: entry, startedAt: input.now, durationMs: TRANSITION_MS },
        }
      } else if (state.mode === 'TRANSITION' && animated(state, input.now).done) {
        next = { ...next, mode: 'WORK', symbol: entry, animation: null }
      }
    } else if (state.symbol === null || state.lastNow === null) {
      // Ouverture ou rechargement pendant une intervention : stabilisé d'emblée, sans animation.
      next = { ...next, mode: 'WORK', workItemId: input.work.itemId, symbol: entry, animation: null }
    } else {
      // Entrée confirmée : le symbole rejoint l'entrée, l'orientation est figée telle qu'elle est.
      next = {
        ...next,
        mode: 'TRANSITION',
        workItemId: input.work.itemId,
        animation: { from: animated(state, input.now).point ?? entry, to: entry, startedAt: input.now, durationMs: TRANSITION_MS },
      }
    }
  } else if (state.mode === 'WORK' || state.mode === 'TRANSITION') {
    // Sortie confirmée (le moteur n'a plus de résidence) : retour progressif vers la position de déplacement.
    const from = animated(state, input.now).point ?? state.symbol
    next = {
      ...next,
      mode: 'LEAVING',
      workItemId: null,
      animation: from && travelPoint ? { from, to: travelPoint, startedAt: input.now, durationMs: LEAVING_MS } : null,
      fallbackAnchor: travelPoint,
    }
    if (!next.animation) next = { ...next, mode: travelPoint ? 'TRAVEL' : 'LEAVING' }
  } else if (state.mode === 'LEAVING' || state.mode === 'PAUSED') {
    const done = animated(state, input.now).done
    if (state.mode === 'PAUSED' || done || !state.animation) next = { ...next, mode: 'TRAVEL', animation: null }
    else if (travelPoint) next = { ...next, animation: { ...state.animation, to: travelPoint } }
  }

  if (next.mode === 'TRAVEL') {
    if (travelPoint) next = { ...next, symbol: travelPoint }
    if (input.gpsFresh && input.progress) {
      next = { ...next, heading: turnToward(state.heading, input.progress.bearing, maxStep) }
      headingSource = 'PATH'
    } else if (input.gpsFresh && input.fix && (input.fix.accuracyM ?? Infinity) <= 20) {
      // Sans parcours : cap mesuré sur un déplacement réel suffisant, jamais sur le cap instantané du GPS.
      const anchor = state.fallbackAnchor
      if (!anchor) next = { ...next, fallbackAnchor: input.fix }
      else if (metersBetween(anchor, input.fix) >= FALLBACK_HEADING_MIN_MOVE_M) {
        const target = bearingOf({ x: 0, y: 0 }, toLocal(anchor, input.fix))
        next = { ...next, heading: turnToward(state.heading, target, maxStep), fallbackAnchor: input.fix }
      }
      headingSource = 'MOVEMENT'
    }
  }

  const position = next.animation ? animated(next, input.now).point : next.symbol
  next = { ...next, symbol: position }
  return {
    state: next,
    output: {
      mode: next.mode,
      symbol: position,
      heading: next.heading,
      stabilized: next.mode === 'WORK' || next.mode === 'TRANSITION' || next.mode === 'PAUSED',
      uncertain: !input.gpsFresh && next.mode !== 'WORK' && next.mode !== 'PAUSED',
      headingSource,
    },
  }
}

// ------------------------------------------------------------------ Cadrage : zone utile et marges

export interface Viewport {
  width: number
  height: number
  /** Zone non recouverte par les fiches et panneaux (px depuis le coin haut gauche du cadre de la carte). */
  useful: { left: number; top: number; right: number; bottom: number }
}

/** Mètres par pixel (projection Web Mercator, carte Google à 256 px par tuile). */
export function metersPerPixel(lat: number, zoom: number): number {
  return (156_543.033_92 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom
}

/** Position d'un point à l'écran (px) relativement au centre, la carte étant orientée selon `heading`. */
export function screenOffset(center: LatLng, point: LatLng, heading: number, zoom: number): { x: number; y: number } {
  const local = toLocal(center, point)
  const scale = metersPerPixel(center.lat, zoom)
  const radians = (heading * Math.PI) / 180
  const right = local.x * Math.cos(radians) - local.y * Math.sin(radians)
  const up = local.x * Math.sin(radians) + local.y * Math.cos(radians)
  return { x: right / scale, y: -up / scale }
}

/** Fraction de la zone utile qui sert de marge : le symbole ne déclenche un recentrage qu'en y entrant. */
export const RECENTER_MARGIN = 0.2
/** Position visée après recentrage, dans la zone utile : bas du tiers central, plus de parcours visible devant. */
export const RECENTER_ANCHOR = { x: 0.5, y: 0.68 } as const

/**
 * Nouveau centre de la carte si le symbole approche une marge de la zone utile; `null` : la carte ne bouge pas (le point
 * avance librement). Aucun recentrage permanent à chaque relevé.
 */
export function recenterIfNeeded(
  center: LatLng,
  symbol: LatLng,
  heading: number,
  zoom: number,
  viewport: Viewport,
): LatLng | null {
  const offset = screenOffset(center, symbol, heading, zoom)
  const x = viewport.width / 2 + offset.x
  const y = viewport.height / 2 + offset.y
  const { left, top, right, bottom } = viewport.useful
  const marginX = (right - left) * RECENTER_MARGIN
  const marginY = (bottom - top) * RECENTER_MARGIN
  if (x >= left + marginX && x <= right - marginX && y >= top + marginY && y <= bottom - marginY) return null
  return centerForAnchor(center, symbol, heading, zoom, viewport)
}

/** Centre qui place le symbole au point d'ancrage de la zone utile (recentrage explicite ou par marges). */
export function centerForAnchor(center: LatLng, symbol: LatLng, heading: number, zoom: number, viewport: Viewport): LatLng {
  const offset = screenOffset(center, symbol, heading, zoom)
  const x = viewport.width / 2 + offset.x
  const y = viewport.height / 2 + offset.y
  const { left, top, right, bottom } = viewport.useful
  const anchorX = left + (right - left) * RECENTER_ANCHOR.x
  const anchorY = top + (bottom - top) * RECENTER_ANCHOR.y
  const scale = metersPerPixel(center.lat, zoom)
  const shiftRight = (x - anchorX) * scale
  const shiftUp = -(y - anchorY) * scale
  const radians = (heading * Math.PI) / 180
  const east = shiftRight * Math.cos(radians) + shiftUp * Math.sin(radians)
  const north = -shiftRight * Math.sin(radians) + shiftUp * Math.cos(radians)
  const cosLat = Math.cos((center.lat * Math.PI) / 180)
  return { lat: center.lat + north / 111_320, lng: center.lng + east / (111_320 * cosLat) }
}
